import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface JobEntityType {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  displayOrder: number;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const jobEntityTypesKeys = {
  all: ['job-entity-types'] as const,
  list: () => [...jobEntityTypesKeys.all, 'list'] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapJobEntityType(item: Record<string, unknown>): JobEntityType {
  return {
    id: item.id as string,
    name: (item.name || '') as string,
    description: (item.description ?? null) as string | null,
    isActive: (item.is_active ?? item.isActive ?? true) as boolean,
    displayOrder: (item.display_order ?? item.displayOrder ?? 0) as number,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Fetch all job entity types for the current tenant.
 * These represent the "job types" that drive dynamic form rendering.
 */
export function useJobEntityTypes() {
  return useQuery({
    queryKey: jobEntityTypesKeys.list(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/job-entity-types');
      const raw = response.data.data || [];
      return raw.map(mapJobEntityType);
    },
  });
}
